import { motion, useReducedMotion, type HTMLMotionProps, type Variants } from "motion/react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Every entrance in the portal is a slide-up: the element starts a short way
 * below its resting position and settles with the same easing curve used by the
 * CSS transitions in styles.css. Keeping the numbers in one place is what makes
 * separately-authored screens feel like one product.
 */
export const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export const slideUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

/** Parent that releases its children one after another. */
export const stagger = (step = 0.09, delay = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: step, delayChildren: delay } },
});

/**
 * Slide-up entrance for a single element.
 *
 * `inView` defers the animation until the element scrolls into the viewport,
 * which is what the marketing sections below the fold want; the default fires
 * on mount for above-the-fold content.
 */
export function FadeUp({
  children,
  className,
  delay = 0,
  duration = 0.55,
  distance = 24,
  inView = false,
  ...rest
}: {
  children: ReactNode;
  className?: string | undefined;
  delay?: number | undefined;
  duration?: number | undefined;
  distance?: number | undefined;
  inView?: boolean | undefined;
} & Omit<HTMLMotionProps<"div">, "children" | "className">) {
  const reduced = useReducedMotion();
  const hidden = reduced ? { opacity: 0, y: 0 } : { opacity: 0, y: distance };
  const visible = { opacity: 1, y: 0 };

  return (
    <motion.div
      className={className}
      initial={hidden}
      {...(inView
        ? { whileInView: visible, viewport: { once: true, amount: 0.25 } }
        : { animate: visible })}
      transition={{ duration, delay, ease: EASE_OUT }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * Slide-up list whose items arrive in sequence. Wrap items in `<StaggerItem>`.
 */
export function StaggerGroup({
  children,
  className,
  step = 0.09,
  delay = 0,
  inView = true,
  ...rest
}: {
  children: ReactNode;
  className?: string | undefined;
  step?: number | undefined;
  delay?: number | undefined;
  inView?: boolean | undefined;
} & Omit<HTMLMotionProps<"div">, "children" | "className">) {
  return (
    <motion.div
      className={className}
      variants={stagger(step, delay)}
      initial="hidden"
      {...(inView
        ? { whileInView: "visible" as const, viewport: { once: true, amount: 0.2 } }
        : { animate: "visible" as const })}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
  duration = 0.55,
  ...rest
}: {
  children: ReactNode;
  className?: string | undefined;
  duration?: number | undefined;
} & Omit<HTMLMotionProps<"div">, "children" | "className">) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={reduced ? { hidden: { opacity: 0 }, visible: { opacity: 1 } } : slideUp}
      transition={{ duration, ease: EASE_OUT }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Landmark elements a page wrapper is allowed to render as. */
const PAGE_TAGS = {
  div: motion.div,
  main: motion.main,
  section: motion.section,
} as const;

/**
 * Page-level entrance. Replaces the old `page-enter` CSS class so route
 * transitions and in-page motion share one timing system.
 */
export function MotionPage({
  children,
  className,
  as = "div",
  ...rest
}: {
  children: ReactNode;
  className?: string | undefined;
  as?: keyof typeof PAGE_TAGS | undefined;
} & Omit<HTMLMotionProps<"div">, "children" | "className">) {
  const reduced = useReducedMotion();
  const Tag = PAGE_TAGS[as];
  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y: reduced ? 0 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE_OUT }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Card that slides up into place and lifts under the cursor. The lift lives in
 * motion (transform) while colour/shadow warm-up lives in the `feature-card`
 * utility, so the two never animate the same property.
 */
export function LiftCard({
  children,
  className,
  lift = -8,
  ...rest
}: {
  children: ReactNode;
  className?: string | undefined;
  lift?: number | undefined;
} & Omit<HTMLMotionProps<"div">, "children" | "className">) {
  const reduced = useReducedMotion();
  // Reduced-motion users still get the card, just without travel — so the
  // hover gestures are dropped entirely rather than passed as undefined.
  const gestures = reduced
    ? {}
    : { whileHover: { y: lift, scale: 1.015 }, whileTap: { scale: 0.995 } };

  return (
    <motion.div
      className={cn("feature-card", className)}
      variants={reduced ? { hidden: { opacity: 0 }, visible: { opacity: 1 } } : slideUp}
      transition={{ duration: 0.6, ease: EASE_OUT }}
      {...gestures}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
